// Phase 3B.3 — Opportunities & Insights engine.
// Pure, deterministic, no LLM, no network. Derives Signal -> Opportunity -> Recommended Action
// from ALREADY-FETCHED markets-data (+ national benchmark as CONTEXT only), respecting
// geo scope and provenance. Never fabricates a value that isn't observed.

import { localizeCountry } from "@/lib/marketFormat";

const BENCH_LEVELS = new Set(["CITY", "REGION"]);

// Provenance quality weight (scoring factor). Benchmark is context-only, Demo excluded.
const PROV_WEIGHT = { "Client Data": 1.0, "Lodge Data": 0.9, ADI: 0.8, Benchmark: 0.6, Demo: 0 };

// Objective -> signal types it makes more relevant (scoring boost + framing). Never a standalone
// signal: an objective alone (without data) produces nothing.
const OBJECTIVE_BOOST = {
  "Increase revenue": ["dominant", "growth"],
  "Find new source markets": ["growth", "air"],
  "Develop low season": [],
  "Improve occupancy": [],
  "Develop distribution": ["growth", "concentration"],
  "Activate partners": ["dominant", "concentration"],
  "Improve visitor dispersion": ["concentration"],
  "Measure performance": [],
};

const layerToSource = (layer) =>
  layer === "CLIENT" ? "Client Data"
    : layer === "LODGE" ? "Lodge Data"
    : layer === "BENCHMARK" ? "Benchmark" : "Demo";

// Single source of truth for the primary action mapped to each signal type (no second mapping engine).
const PRIMARY_ACTION = { growth: "campaign", decline: "campaign", dominant: "explore", concentration: "partners", air: "explore", observed: "explore", benchmark_gap: "explore" };

const fmtInt = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(Math.round(n));
const pctOf = (share) => Math.round(share * 100);
const signPct = (v) => `${v > 0 ? "+" : ""}${v} %`;
const normalizeMarketId = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-zA-Z0-9]/g, "")
  .toUpperCase();

const matchesFocusedMarket = (opportunity, focusMarket) => {
  if (!focusMarket) return true;
  const routeOrigin = String(focusMarket.air_market_key || "").split("-")[0];
  const focusValues = [
    focusMarket.key,
    focusMarket.iso,
    focusMarket.name,
    focusMarket.raw,
    routeOrigin,
  ].map(normalizeMarketId).filter(Boolean);
  const opportunityValues = [opportunity.marketKey, opportunity.market]
    .map(normalizeMarketId)
    .filter(Boolean);
  return opportunityValues.some((value) => focusValues.includes(value));
};

function buildSignal(type, r, share, prov, objBoost, extra = {}) {
  // The API keeps canonical English country names, while the French product UI
  // must carry its localized label all the way into actions and campaign briefs.
  const name = localizeCountry(r.name);
  // Keep a canonical route when one is available so every downstream CTA opens
  // the exact market that produced the signal (for example IN-MU, not /markets/IN).
  const key = r.air_market_key || r.key || r.iso;
  const evo = r.evolution;
  const pct = pctOf(share);
  const objMatch = objBoost.has(type) ? 1 : 0;

  let dynamics;
  if (type === "growth" || type === "decline" || type === "benchmark_gap") dynamics = Math.min(Math.abs(evo) / 50, 1);
  else if (type === "concentration" || type === "dominant") dynamics = Math.min(share, 1);
  else dynamics = type === "observed" ? 0.25 : 0.4; // observed / air
  const sizeF = Math.min(share, 1);
  const provW = PROV_WEIGHT[prov] ?? 0;

  // Deterministic, explainable score. lodge_score is a light tie-breaker only (<=~0.1), never the decision.
  let score = 100 * (0.4 * sizeF + 0.3 * dynamics + 0.15 * objMatch + 0.15 * provW);
  if (type === "benchmark_gap") score += 3; // local signal corroborated by national context ranks above the plain signal
  if (r.lodge_score != null) score += (r.lodge_score || 0) / 1000;

  const confidence = score >= 70 ? "High" : score >= 45 ? "Medium" : "Low";

  const texts = {
    growth: {
      signal: `${name} ${signPct(evo)}`,
      evidence: `${signPct(evo)} · 6 derniers mois vs 6 précédents · ${pct}% du volume · ${prov}`,
      opportunity: `Renforcer le marché ${name}`,
      recommendedAction: `Explorer le marché ${name}`,
    },
    decline: {
      signal: `${name} ${signPct(evo)}`,
      evidence: `${signPct(evo)} · 6 derniers mois vs 6 précédents · ${pct}% du volume · ${prov}`,
      opportunity: `Enrayer le recul sur ${name}`,
      recommendedAction: `Analyser et reconquérir ${name}`,
    },
    dominant: {
      signal: `${name} · ${pct}% du volume`,
      evidence: `Marché source principal · ${pct}% du volume${evo != null ? ` · ${signPct(evo)} sur les 6 derniers mois vs 6 précédents` : ""} · ${prov}`,
      opportunity: `Capitaliser sur votre marché principal ${name}`,
      recommendedAction: `Explorer le marché ${name}`,
    },
    concentration: {
      signal: `Concentration ${pct}% sur ${name}`,
      evidence: `${pct}% du volume concentré sur ${name} · dépendance élevée · ${prov}`,
      opportunity: `Diversifier au-delà de ${name}`,
      recommendedAction: `Identifier de nouveaux marchés & partenaires`,
    },
    air: {
      signal: `${name} · potentiel aérien`,
      evidence: `Flux aérien inbound observé (${fmtInt(r.air_passengers)} pax) · ${pct}% du volume touristique · ADI`,
      opportunity: `Convertir le flux aérien ${name} en demande`,
      recommendedAction: `Explorer le marché ${name}`,
    },
    observed: {
      signal: `${name} · ${fmtInt(r.volume)} arrivées`,
      evidence: `Volume touristique observé (${fmtInt(r.volume)}) · ${prov}`,
      opportunity: `Approfondir le potentiel du marché ${name}`,
      recommendedAction: `Explorer le marché ${name}`,
    },
    benchmark_gap: {
      signal: `${name} surperforme le national`,
      evidence: `Local ${signPct(evo)} vs national ${signPct(extra.benchEvo)} · 6 derniers mois vs 6 précédents · ${prov} (contexte Benchmark)`,
      opportunity: `Accélérer sur ${name}, plus dynamique que le contexte national`,
      recommendedAction: `Explorer le marché ${name}`,
    },
  }[type];

  return {
    id: `${type}-${key}`,
    type,
    market: name,
    marketKey: key,
    period: r.period_start && r.period_end ? `${r.period_start} → ${r.period_end}` : (r.period || ""),
    source: prov,
    dataUsed: [prov],
    confidence,
    primaryAction: PRIMARY_ACTION[type] || "explore",
    _score: score,
    ...texts,
  };
}

/**
 * deriveOpportunities — pure function.
 * @param {Array}  markets     markets-data rows (already resolved per-block: Client > Lodge PRIMARY).
 * @param {Object} benchmark   benchmark-data payload ({markets:[...]}) — CONTEXT only (CITY/REGION).
 * @param {Array}  objectives  org.objectives.
 * @param {String} sourceLabel default provenance label ("Client Data" | "Lodge Data" | "Demo").
 * @param {String} geoLevel    org.geographic_level (COUNTRY | REGION | CITY | null).
 * @param {Object} focusMarket optional selected market row; keeps scoring global but returns only
 *                             the matching market opportunity before applying the display cap.
 * @returns {{opportunities:Array, isDemo:boolean}}
 */
export function deriveOpportunities({ markets = [], benchmark = null, objectives = [], sourceLabel = "Demo", geoLevel = null, focusMarket = null } = {}) {
  const rows = (markets || []).filter((r) => (r.volume || 0) > 0 || r.evolution != null);
  const totalVolume = rows.reduce((s, r) => s + (r.volume || 0), 0);
  if (!rows.length || totalVolume <= 0) return { opportunities: [], isDemo: true };

  // Micro-market guard: a market must weigh at least MIN_SHARE of the TOTAL observed volume to
  // emit a per-market signal. Relative & explainable (no arbitrary absolute number); filters out
  // tiny markets whose % swings are noise (e.g. a market at ~1% of volume with -100%).
  // Share is always volume / total observed volume (NOT `part_destination`, which is a different
  // per-market destination metric and not a portfolio share).
  const MIN_SHARE = 0.05;
  const share = (r) => (totalVolume ? (r.volume || 0) / totalVolume : 0);
  const objBoost = new Set(objectives.flatMap((o) => OBJECTIVE_BOOST[o] || []));

  const benchByKey = {};
  (benchmark?.markets || []).forEach((b) => { benchByKey[b.iso || b.name] = b; });

  const sorted = rows.slice().sort((a, b) => (b.volume || 0) - (a.volume || 0));
  const top = sorted[0];
  const topShare = share(top);
  const cand = [];

  for (const r of sorted) {
    const sh = share(r);
    const prov = layerToSource(r.source_layer);
    const evo = r.evolution;

    if (evo != null && evo >= 10 && sh >= MIN_SHARE) cand.push(buildSignal("growth", r, sh, prov, objBoost));
    if (evo != null && evo <= -10 && sh >= MIN_SHARE) cand.push(buildSignal("decline", r, sh, prov, objBoost));
    if (r.air_passengers != null && r.air_passengers > 0 && sh < 0.5) cand.push(buildSignal("air", r, sh, "ADI", objBoost));

    // Local vs benchmark: CITY/REGION only, requires LOCAL data to exist (never benchmark-alone).
    if (BENCH_LEVELS.has(geoLevel) && evo != null && sh >= MIN_SHARE) {
      const b = benchByKey[r.iso || r.name];
      if (b && b.evolution != null && evo - b.evolution >= 8) {
        cand.push(buildSignal("benchmark_gap", r, sh, prov, objBoost, { benchEvo: b.evolution }));
      }
    }
  }

  // Dominant / concentration attach to the top market (concentration takes over at >=60%).
  if (top) {
    const prov = layerToSource(top.source_layer);
    cand.push(buildSignal(topShare >= 0.6 ? "concentration" : "dominant", top, topShare, prov, objBoost));
  }

  // Deduplicate: keep the single highest-scoring signal per market, then cap at 5.
  const bestByMarket = {};
  for (const c of cand) {
    if (!bestByMarket[c.market] || c._score > bestByMarket[c.market]._score) bestByMarket[c.market] = c;
  }
  const ranked = Object.values(bestByMarket).sort((a, b) => b._score - a._score);
  let opportunities = ranked.filter((opportunity) => matchesFocusedMarket(opportunity, focusMarket)).slice(0, 5);

  // The destination-wide 5% guard must not blank a market the user explicitly
  // selected. In focused mode, produce one honest signal from that market's own
  // observed values when the global ranking did not retain it.
  if (focusMarket && opportunities.length === 0) {
    const focused = rows.find((row) => matchesFocusedMarket({
      marketKey: row.air_market_key || row.key || row.iso,
      market: row.name,
    }, focusMarket)) || (((focusMarket.volume || 0) > 0 || focusMarket.evolution != null) ? focusMarket : null);
    if (focused) {
      const focusedShare = share(focused);
      const focusedProv = layerToSource(focused.source_layer);
      const focusedType = focused.evolution != null && focused.evolution <= -10 ? "decline"
        : focused.evolution != null && focused.evolution >= 10 ? "growth"
          : focused.air_passengers > 0 ? "air"
            : focused.volume > 0 ? "observed" : null;
      if (focusedType) {
        opportunities = [buildSignal(
          focusedType,
          focused,
          focusedShare,
          focusedType === "air" ? "ADI" : focusedProv,
          objBoost,
        )];
      }
    }
  }
  return { opportunities, isDemo: ranked.length === 0 && opportunities.length === 0 };
}

/**
 * Build the destination portfolio without the five-card display cap used by the
 * historical opportunity grid. Each supplied market keeps its own observed
 * signal (including small markets explicitly present in the destination
 * perimeter), then the complete portfolio is ranked with the same explainable
 * score as the focused market view.
 */
export function deriveOpportunityPortfolio({
  markets = [],
  marketScope = null,
  benchmark = null,
  objectives = [],
  sourceLabel = "Demo",
  geoLevel = null,
} = {}) {
  const rows = (markets || []).filter((row) =>
    (row?.volume || 0) > 0 || row?.evolution != null || (row?.air_passengers || 0) > 0);
  const scopedRows = Array.isArray(marketScope) ? marketScope.filter(Boolean) : rows;
  const opportunities = scopedRows
    .map((focusMarket) => deriveOpportunities({
      markets: rows,
      benchmark,
      objectives,
      sourceLabel,
      geoLevel,
      focusMarket,
    }).opportunities[0] || null)
    .filter(Boolean)
    .sort((a, b) => b._score - a._score);

  return { opportunities, isDemo: opportunities.length === 0 };
}

// KPI hint when converting an opportunity into a campaign (mirrors /opportunities behavior).
export const suggestOppKpi = (type) => ({
  growth: "Réservations incrémentales",
  decline: "Réservations reconquises",
  dominant: "Revenu incrémental",
  concentration: "Nouveaux marchés activés",
  air: "Taux de conversion (%)",
  observed: "Engagement qualifié",
  benchmark_gap: "Réservations incrémentales",
}[type] || "Réservations");
